import {t} from '@/i18n'
import {ColoredOption} from '@/types/ColoredOption'
import {optionOf, optionsOf, reaches} from '@/types/Ladder'
import {Person} from '@/types/Person'

/**
 * O nível de acesso de alguém num ambiente: Visualizador, Editor, Designer, Administrador.
 *
 * Era o "papel" (`role`), e virou "nível de acesso" (`accessLevel`) quando chegaram os PAPÉIS do
 * ambiente — Developer, Tester, Product Owner (decisão do usuário): o nível diz o que a pessoa pode
 * fazer no lunedi, e os papéis, o que ela faz no trabalho, que as fórmulas leem. O mesmo nome nos
 * dois lados — tela, API, banco e código.
 *
 * Esta escada está escrita DUAS VEZES, uma em cada repositório — aqui e no `accessLevels.ts`
 * do servidor —, porque são dois projetos sem pacote comum. O de lá é a garantia; este é o
 * desenho. É a mesma postura do encerramento, e quem mudar um tem de mudar o outro.
 */

/**
 * Os níveis, **em ordem crescente de poder**. A ordem É a regra: a escada é cumulativa, então
 * quem desenha a coleção também usa os dados dela, e quem administra pode tudo.
 */
export const ACCESS_LEVELS = ['viewer', 'editor', 'designer', 'admin'] as const
export type AccessLevel = typeof ACCESS_LEVELS[number]

/**
 * Uma pessoa do ambiente, como a tela de acesso a vê: o nível, e os aliases dos papéis dela
 * (ausente é nenhum).
 */
export type Member = Person & {accessLevel: AccessLevel, roles?: string[]}

/**
 * O nível basta para o que se exige?
 *
 * Nível ausente não basta para nada: `undefined` chega aqui quando o ambiente veio de um
 * servidor que ainda não mandava o nível, e tratá-lo como o mais fraco já é o certo — a tela
 * desenha de menos, e quem garante é o portão.
 */
export function allows(level: AccessLevel | undefined, needed: AccessLevel): boolean {
    return reaches(ACCESS_LEVELS, level, needed)
}

/**
 * A cor de cada nível: o chip dele na aba Acessos e no menu da conta, onde cada um vê o próprio nível
 * no ambiente. Já foi a cor da barra que o nível destravava — ciano no design, roxo no acesso —,
 * quando a casca tinha um modo para cada; os modos saíram, e a barra ficou com o índigo do editor
 * (ver o `BAR_COLOR`).
 *
 * O visualizador é cinza: é o nível que não pode mudar nada.
 *
 * Cor base, sem tom: quem desenha deriva o seu — o chip clareia o fundo e escurece o texto pelo
 * `shade`, a barra usa o `darken-4`. Guardar o tom aqui obrigaria cada consumidor a desfazê-lo.
 */
export const LEVEL_COLOR: Record<AccessLevel, string> = {
    admin: 'deep-purple',
    designer: 'cyan',
    editor: 'indigo',
    viewer: 'blue-grey'
}

export function levelName(level: AccessLevel): string {
    return t(`access-level.${level}`) as string
}

/**
 * O nível como valor colorido, para desenhar com `form: 'chip'`.
 *
 * **Chip, e não bolinha nem régua**: nível é opção de um domínio fechado de quatro, e a forma
 * diz a natureza do valor — está escrito no `ValueForm`. O `id` é a posição na escada, que é o
 * que o `ColoredOption` pede e também a ordem em que eles se listam.
 */
export function levelOption(level: AccessLevel): ColoredOption {
    return optionOf(ACCESS_LEVELS, LEVEL_COLOR, levelName, level)
}

/** Os quatro níveis para escolher, do mais fraco ao mais forte. */
export function levelOptions(): Array<ColoredOption & {value: AccessLevel}> {
    return optionsOf(ACCESS_LEVELS, LEVEL_COLOR, levelName)
}
